import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, ShieldCheck, IndianRupee } from "lucide-react";

export default function OrderCheckout() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [svc, setSvc] = useState(null);
  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);
  const [paying, setPaying] = useState(false);
  const [order, setOrder] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    email: user?.email || "",
    mobile: user?.mobile || "",
    pan: user?.pan || "",
    address: user?.address || "",
    additional_info: "",
    has_gstin: false,
    gstin: "",
    billing_state: "Rajasthan",
  });

  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => setSvc(r.data.service));
  }, [slug]);

  const submitDetails = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/orders", { service_slug: slug, ...form });
      // save billing info (GST + state) to compute IGST vs CGST+SGST
      try {
        await api.put(`/orders/${data.order.id}/billing`, {
          order_id: data.order.id,
          has_gstin: !!form.has_gstin,
          gstin: form.gstin || "",
          billing_state: form.billing_state,
        });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Billing info invalid");
        setCreating(false);
        return;
      }
      setOrder(data.order);
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create order");
    } finally {
      setCreating(false);
    }
  };

  const proceedPayment = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/cashfree/create", { order_id: order.id, coupon_code: couponApplied });
      const { load } = await import("@cashfreepayments/cashfree-js");
      const cashfree = await load({ mode: data.mode === "production" ? "production" : "sandbox" });
      await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment initialisation failed");
      setPaying(false);
    }
  };

  const applyCoupon = async () => {
    if (!coupon) return;
    try {
      const { data } = await api.post(`/coupons/validate?code=${encodeURIComponent(coupon)}&amount=${order?.amount || svc.price}`);
      setCouponApplied(data.code);
      setCouponDiscount(data.discount);
      toast.success(`Coupon applied — you save ₹${data.discount}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid coupon");
      setCouponApplied(""); setCouponDiscount(0);
    }
  };

  if (!svc) return <DashboardLayout><div className="text-slate-500">Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="mb-6 flex items-center gap-3 text-sm text-slate-500">
        <span onClick={() => nav("/services")} className="cursor-pointer hover:text-[#1E3A8A]">Services</span>
        <span>/</span>
        <span className="text-[#1E3A8A] font-medium">{svc.name}</span>
        <span>/</span>
        <span className="font-medium">Checkout</span>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="section-label">Step {step} of 2</div>
            <h1 className="mt-1 font-display font-bold text-2xl text-[#0A192F]">
              {step === 1 ? "Service details" : "Confirm & Pay"}
            </h1>

            {step === 1 && (
              <form onSubmit={submitDetails} className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="checkout-details-form">
                <div>
                  <Label>Full Name</Label>
                  <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="checkout-name" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="checkout-email" />
                </div>
                <div>
                  <Label>Mobile</Label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-slate-50 text-slate-700 text-sm font-medium">+91</span>
                    <Input
                      required
                      value={form.mobile}
                      onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                      placeholder="9876543210"
                      inputMode="numeric"
                      maxLength={10}
                      className="rounded-l-none"
                      data-testid="checkout-mobile"
                    />
                  </div>
                </div>
                <div>
                  <Label>PAN Number</Label>
                  <Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" className="font-mono uppercase" data-testid="checkout-pan" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Address</Label>
                  <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="checkout-address" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Additional Information (optional)</Label>
                  <Textarea rows={3} value={form.additional_info} onChange={(e) => setForm({ ...form, additional_info: e.target.value })} placeholder="Any specific notes about your filing…" data-testid="checkout-notes" />
                </div>

                <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="billing-panel">
                  <div className="text-xs uppercase tracking-widest text-slate-500">GST invoice details</div>
                  <div className="flex items-center gap-4 text-sm">
                    <label className="inline-flex items-center gap-2"><input type="radio" name="hasgst" checked={!form.has_gstin} onChange={() => setForm({ ...form, has_gstin: false, gstin: "" })} data-testid="billing-no-gst" /> Personal (No GST)</label>
                    <label className="inline-flex items-center gap-2"><input type="radio" name="hasgst" checked={form.has_gstin} onChange={() => setForm({ ...form, has_gstin: true })} data-testid="billing-has-gst" /> Business (I have GSTIN)</label>
                  </div>
                  {form.has_gstin && (
                    <div>
                      <Label>GSTIN</Label>
                      <Input value={form.gstin || ""} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="27ABCDE1234F1Z5" className="font-mono uppercase" data-testid="billing-gstin-input" maxLength={15} />
                    </div>
                  )}
                  <div>
                    <Label>Billing State (for tax calculation)</Label>
                    <select className="w-full border rounded-md h-10 px-2 bg-white" value={form.billing_state || "Rajasthan"} onChange={(e) => setForm({ ...form, billing_state: e.target.value })} data-testid="billing-state-select">
                      {["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Andaman & Nicobar","Chandigarh","Dadra & Nagar Haveli","Daman & Diu","Lakshadweep","Puducherry"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <div className="text-xs text-slate-500 mt-1">Rajasthan customers → CGST + SGST (9%+9%). Other states → IGST (18%).</div>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Button type="submit" disabled={creating} className="bg-[#1E3A8A] hover:bg-[#1E40AF] h-11 px-6" data-testid="checkout-continue-btn">
                    {creating ? "Creating…" : "Continue to Payment"} <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </form>
            )}

            {step === 2 && order && (
              <div className="mt-6" data-testid="checkout-pay-step">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs uppercase tracking-widest text-slate-500">Review your order</div>
                  <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-500">Name:</span> <span className="font-medium">{order.full_name}</span></div>
                    <div><span className="text-slate-500">Email:</span> <span className="font-medium">{order.email}</span></div>
                    <div><span className="text-slate-500">Mobile:</span> <span className="font-medium">{order.mobile}</span></div>
                    <div><span className="text-slate-500">PAN:</span> <span className="font-mono font-medium">{order.pan || "—"}</span></div>
                    <div className="sm:col-span-2"><span className="text-slate-500">Address:</span> <span className="font-medium">{order.address || "—"}</span></div>
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4 flex items-start gap-3" data-testid="payment-provider-banner">
                  <ShieldCheck className="w-5 h-5 text-[#1E3A8A] mt-0.5 shrink-0" />
                  <div className="text-sm text-slate-700">
                    Secure payment via <span className="font-medium">Cashfree Payments</span>. Your card details never touch our servers — status is verified server-side before your order is marked paid.
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4" data-testid="coupon-panel">
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Have a coupon?</div>
                  {couponApplied ? (
                    <div className="flex items-center justify-between text-sm">
                      <div>
                        <span className="inline-block bg-emerald-50 border border-emerald-200 text-emerald-700 font-mono text-xs px-2 py-1 rounded mr-2">{couponApplied}</span>
                        <span className="text-slate-700">Applied — you save <b>₹{couponDiscount}</b></span>
                      </div>
                      <button className="text-xs text-red-600 hover:underline" onClick={() => { setCouponApplied(""); setCouponDiscount(0); setCoupon(""); }} data-testid="coupon-remove-btn">Remove</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="e.g. FREEDOM15" className="uppercase" data-testid="coupon-input" />
                      <Button type="button" variant="outline" onClick={applyCoupon} data-testid="coupon-apply-btn">Apply</Button>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <Button onClick={proceedPayment} disabled={paying} className="bg-[#1E3A8A] hover:bg-[#1E40AF] h-11 px-6" data-testid="checkout-pay-btn">
                    <IndianRupee className="w-4 h-4 mr-1" /> {paying ? "Processing…" : `Pay ₹${Math.max(0, (order.amount || svc.price) - couponDiscount)}`}
                  </Button>
                  <Button variant="outline" className="h-11" onClick={() => setStep(1)} data-testid="checkout-back-btn">← Edit details</Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="rounded-xl border border-slate-200 bg-white p-6 sticky top-24">
            <div className="section-label">Order summary</div>
            <div className="mt-3 font-display font-semibold text-lg text-[#0A192F]">{svc.name}</div>
            <div className="text-sm text-slate-600 mt-1">{svc.short_description}</div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Service fee</span><span>₹{svc.price}</span></div>
              <div className="flex justify-between text-slate-500 text-xs"><span>Taxes included</span><span>—</span></div>
              <div className="border-t border-slate-100 pt-3 flex justify-between font-semibold">
                <span>Total</span><span>₹{svc.price}</span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Required documents</div>
              <ul className="space-y-1.5">
                {(svc.required_documents || []).map((d) => (
                  <li key={d} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /> {d}
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-xs text-slate-500">You'll upload these after payment.</div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
